// superuserLibrary.js — a superuser's Library (V2 #97, on the backend's #94).
//
// A member's Library is untouched: GET /library/cartons, as before. A
// superuser's comes from GET /library/browse, which
//   - answers with the view they last chose when it is sent NO view
//     (scope / organisation / drafts), so the choice is remembered per user;
//   - stores and RECORDS a view when one is sent (decision 5 on #92). So the
//     view is sent only when the user changes it — never on a plain refresh,
//     which would also overwrite a remembered choice with the default;
//   - filters, searches, sorts and pages within the view; none of that is a
//     view, and none of it is recorded.
// Transport-free, so the rules below are tested through their generated
// mirror (src/superuserlibrary.test.js); the fetchers live in library.js.

export const SCOPES = [
  { value: 'mine', label: 'My library' },
  { value: 'org', label: 'One organisation' },
  { value: 'all', label: 'All organisations' },
];
export const STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Drafts' },
  { value: 'private', label: 'Private' },
  { value: 'shared', label: 'Shared' },
];
export const KINDS = [
  { value: '', label: 'Any kind' },
  { value: 'pdf', label: 'PDF' },
  { value: 'two_slot', label: 'Two-file' },
  { value: 'parametric', label: 'Blank' },
];
export const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'name', label: 'By name' },
  { value: 'owner', label: 'By owner' },
];
export const PAGE_SIZE = 48;          // a whole number of rows at 2, 3, 4 or 6 cards
export const NO_FILTERS = { owner: null, ownerName: '', status: '', kind: '', duplicatesOnly: false, sort: 'newest', q: '' };

export function isSuperuser(me) {
  return !!me && me.is_superuser === true;
}

/** A view in its one canonical form, as the backend keeps it: an unreadable
 *  one, or "one organisation" with none named, is the member view. */
export function normaliseView(view) {
  const v = view || {};
  if (v.scope === 'all' || (v.scope === 'org' && v.org_id)) {
    return { scope: v.scope, org_id: v.scope === 'org' ? v.org_id : null, include_drafts: !!v.include_drafts };
  }
  return { scope: 'mine', org_id: null, include_drafts: false };
}

export function sameView(a, b) {
  const x = normaliseView(a), y = normaliseView(b);
  return x.scope === y.scope && x.org_id === y.org_id && x.include_drafts === y.include_drafts;
}

/**
 * The query string for GET /library/browse. `view` is sent only when the user
 * has just CHOSEN one (it is then stored and recorded); omitted, the backend
 * answers with the remembered view. Filters are sent only when set.
 */
export function browseQuery({ surface = 'designer', view = null, filters = {}, page = 1, pageSize = PAGE_SIZE } = {}) {
  const p = new URLSearchParams();
  p.set('surface', surface);
  if (view) {
    const v = normaliseView(view);
    p.set('scope', v.scope);
    if (v.scope === 'org') p.set('org_id', v.org_id);
    if (v.scope !== 'mine') p.set('include_drafts', v.include_drafts ? 'true' : 'false');
  }
  const f = filters || {};
  if (f.owner) p.set('owner', f.owner);
  if (f.status) p.set('status', f.status);
  if (f.kind) p.set('kind', f.kind);
  if (f.duplicatesOnly) p.set('duplicates_only', 'true');
  const q = (f.q || '').trim();
  if (q) p.set('q', q);
  if (f.sort && f.sort !== 'newest') p.set('sort', f.sort);
  if (page > 1) p.set('page', String(page));
  p.set('page_size', String(pageSize));
  return `?${p.toString()}`;
}

/** The view in words, for the Library's bar and "Load from library". */
export function viewLabel(view, orgsById = {}) {
  const v = normaliseView(view);
  if (v.scope === 'mine') return 'My library';
  const where = v.scope === 'all' ? 'All organisations' : ((orgsById[v.org_id] || {}).name || 'One organisation');
  return v.include_drafts ? `${where} · other people’s drafts included` : where;
}

/** "49–96 of 528" for the pager; "No cartons" when there are none. */
export function pagerText({ page = 1, page_size: size = PAGE_SIZE, total = 0 } = {}) {
  if (!total) return 'No cartons';
  const from = (page - 1) * size + 1;
  return from > total ? `${total} in all` : `${from}–${Math.min(page * size, total)} of ${total}`;
}

export function pageCount({ page_size: size = PAGE_SIZE, total = 0 } = {}) {
  return Math.max(1, Math.ceil(total / size));
}

/** Who owns a carton and where, on a superuser's card. */
export function ownerLine(item) {
  return [item && item.owner_name, item && item.org_name].filter(Boolean).join(' · ');
}

/** How a superuser's card marks a carton that is not theirs: muted, and a
 *  draft named as its owner's. A member's cards never use this. */
export function cardMarks(carton, me) {
  const mine = !!carton && !!me && carton.owner_id === me.id;
  const owner = (carton && carton.owner_name) || 'Someone else';
  return { muted: !mine, draftLabel: mine ? 'Draft' : `${owner}’s draft` };
}

/** "Load from library" offers library cartons, not bench drafts. */
export function libraryOnly(items) {
  return (items || []).filter((c) => c && c.origin !== 'draft');
}

/** The duplicate line on a PAGED list: the duplicates on this page by name
 *  (each one can be shown), the rest counted — the badge never depends on
 *  which page a duplicate happens to fall on. */
export function duplicateLine(carton, byId) {
  const ids = carton && Array.isArray(carton.duplicate_of) ? carton.duplicate_of : [];
  const shown = ids.filter((id) => byId && byId[id]).map((id) => ({ id, name: byId[id].name }));
  return { shown, elsewhere: ids.length - shown.length };
}
